import { Box, Button, CheckboxPillGroup, Flex, type CheckboxPillOption } from '@tempmee/react';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAppSelector } from '~/hooks/useAppSelector';
import { SoftwareUtils } from '~/utils/SoftwareUtils';
import type { ISignUpFormValues } from '../../types';
import { POPULAR_SOFTWARES } from '../constants';
import { useSoftwareExpansion } from '../useSoftwareExpansion';
import { SectionHeading } from './SectionHeading';

const SECTION_GAP = 32;
const HEADING_MARGIN_BOTTOM = 16;
const toPill = (label: string): CheckboxPillOption => ({ label, value: label });

export const OfficeSoftwareSection = () => {
  const { control, formState } = useFormContext<ISignUpFormValues>();
  const { showAll, toggle } = useSoftwareExpansion();

  const officeEducation = useAppSelector(
    (state) => state.user.officeEducation as { softwares?: string[] | null } | null | undefined,
  );

  const translatedSoftwares = useMemo(
    () => SoftwareUtils.getSoftwareTranslations(officeEducation?.softwares ?? []),
    [officeEducation?.softwares],
  );

  const options = useMemo<CheckboxPillOption[]>(() => {
    if (!translatedSoftwares.length) return [];
    if (showAll) return translatedSoftwares.map(toPill);
    const available = new Set(translatedSoftwares);
    return POPULAR_SOFTWARES.filter((name) => available.has(name)).map(toPill);
  }, [translatedSoftwares, showAll]);

  return (
    <Box style={{ marginTop: SECTION_GAP }}>
      <SectionHeading marginBottom={HEADING_MARGIN_BOTTOM}>
        What Office Software Is Used at Your Practice?
      </SectionHeading>
      <Controller
        name="softwares"
        control={control}
        rules={{ validate: (value) => Array.isArray(value) && value.length > 0 }}
        render={({ field }) => (
          <CheckboxPillGroup
            data-cy="checkbox-group-softwares"
            options={options}
            value={field.value}
            onChange={field.onChange}
            hasError={Boolean(formState.errors.softwares)}
          />
        )}
      />
      <Flex justify="end" style={{ marginTop: 8 }}>
        <Button
          data-cy="btn-toggle-softwares"
          type="button"
          variant="ghost"
          size="inline"
          onClick={toggle}
          style={{ textDecoration: 'none' }}
        >
          {showAll ? 'View Less' : 'View All'}
        </Button>
      </Flex>
    </Box>
  );
};
